/**
 * Returns the price difference between two specific variants of a product:
 * the base variant and the upgrade variant. A difference of 0 is intentionally
 * rendered as "+$0"; only a negative (or uncalculable) difference returns null
 * so that it doesn't show up in the UI.
 *
 * For example, if you have an option called "Cover" and two variants:
 * "Normal" and "Cooling", then this function will return the price difference
 * between if you select "Normal" versus "Cooling".
 *
 * Meant to be used with the ColumnSelector subcomponents.
 *
 * @param shopifyProduct - The shopify product object
 * @param selectedVariant - The selected variant, we want selectedOptions from it
 * @param baseOptionValue - The option value considered the base option value
 * @param upgradeOptionValue - The option value considered the upgrade option value
 * @param option - The shopify option object giving us the name of the option
 */
export default function calculatePriceDifference(
  shopifyProduct,
  selectedVariant,
  baseOptionValue,
  upgradeOptionValue,
  option
) {
  // Get the options that are not the option we are interested in
  // Only include otherOptions that actually exist in the variant's selectedOptions
  const otherOptions = selectedVariant.selectedOptions.filter(
    (o) =>
      o.name !== option.name &&
      // Only keep if this option exists in at least one variant's selectedOptions
      shopifyProduct.variants.some((v) =>
        v.selectedOptions.some((vo) => vo.name === o.name)
      )
  );

  const findSelectedButWithSpecificOption = (variant, optionValue) => {
    // For each "other" option, check if this variant has the same value for that option
    const matchesOther = otherOptions.every((opt) =>
      variant.selectedOptions.some(
        (o) => o.name === opt.name && o.value === opt.value
      )
    );

    // For the target option, check if this variant has the correct value
    const matchesSpecific = variant.selectedOptions.some(
      (opt) => opt.name === option.name && opt.value === optionValue
    );
    return matchesOther && matchesSpecific;
  };

  // Get the matching variants for the base and upgrade options
  const baseVariant = shopifyProduct.variants.find((v) =>
    findSelectedButWithSpecificOption(v, baseOptionValue)
  );
  const upgradeVariant = shopifyProduct.variants.find((v) =>
    findSelectedButWithSpecificOption(v, upgradeOptionValue)
  );

  try {
    const diff =
      parseFloat(upgradeVariant?.price?.amount) -
      parseFloat(baseVariant?.price?.amount);
    if (diff >= 0) {
      return `+$${Math.round(diff)}`;
    }
  } catch (error) {
    console.error('Error calculating price difference:', error);
  }

  return null;
}

/**
 * `calculatePriceDifference` with the failure handling both column selectors
 * want: a missing price should hide the price label, not blank the selector.
 *
 * @param source Component name, only used to label the console error
 * @returns The formatted difference, or '' if it could not be calculated
 */
export function safePriceDifference(source, ...args) {
  try {
    return calculatePriceDifference(...args) ?? '';
  } catch (error) {
    console.error(`[${source}] Error calculating price difference:`, error);
    return '';
  }
}
